/**
 * /agentic-ops — Agentic Ops mini-course home.
 *
 * The operations companion to Agentic AI Mastery. Lists the five modules with
 * lock badges (OPS.1 free, OPS.2–5 all_access). Content served by the isolated
 * /api/v1/agentic-ops router.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation, Trans } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
  CircularProgress,
  Alert,
  useTheme,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { getAgenticOpsCurriculum } from "../api/client";

export default function AgenticOpsHome() {
  const theme = useTheme();
  const { t } = useTranslation("pages-core");
  const {
    data: curriculum,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["agentic-ops-curriculum"],
    queryFn: getAgenticOpsCurriculum,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !curriculum) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">{t("AgenticOpsHome.errorLoad")}</Alert>
      </Container>
    );
  }

  const modules = curriculum.milestones.flatMap((m) => m.modules);
  const first = modules[0];

  return (
    <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
      <Typography
        variant="overline"
        sx={{ color: theme.palette.primary.main, fontWeight: 700, letterSpacing: "0.12em" }}
      >
        {t("AgenticOpsHome.eyebrow")}
      </Typography>
      <Typography variant="h3" fontWeight={700} sx={{ mt: 1, mb: 2 }}>
        {curriculum.title}
      </Typography>
      <Typography variant="h6" fontWeight={400} color="text.secondary" sx={{ maxWidth: 640, lineHeight: 1.5 }}>
        {curriculum.subtitle}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mt: 2, maxWidth: 680 }}>
        <Trans
          t={t}
          i18nKey="AgenticOpsHome.prereqBody"
          components={{
            strong: <strong />,
            mastery: <RouterLink to="/agentic-ai-mastery" />,
          }}
        />
      </Typography>

      <Typography variant="h5" fontWeight={700} sx={{ mt: 6, mb: 2 }}>
        {t("AgenticOpsHome.modulesHeading")}
      </Typography>
      <Stack spacing={2}>
        {modules.map((m) => (
          <Paper
            key={m.slug}
            component={RouterLink}
            to={`/agentic-ops/module/${m.slug}`}
            elevation={0}
            sx={{
              p: 3,
              display: "block",
              textDecoration: "none",
              color: "inherit",
              border: `1px solid ${theme.palette.divider}`,
              borderLeft: `3px solid ${m.locked ? theme.palette.divider : theme.palette.primary.main}`,
              borderRadius: 2,
              transition: "border-color .15s",
              "&:hover": { borderColor: theme.palette.primary.main },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ color: theme.palette.primary.main, fontFamily: "monospace" }}
              >
                {m.number}
              </Typography>
              {m.locked ? (
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
                  label={t("AgenticOpsHome.lockedBadge")}
                  sx={{ height: 22 }}
                />
              ) : (
                <Chip size="small" variant="outlined" color="primary" label={t("AgenticOpsHome.freeBadge")} sx={{ height: 22 }} />
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: "auto", fontFamily: "monospace" }}
              >
                ~{m.studyTime}
              </Typography>
            </Stack>
            <Typography variant="h6" fontWeight={700}>
              {m.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {m.tagline}
            </Typography>
          </Paper>
        ))}
      </Stack>

      {first && (
        <Box sx={{ mt: 5 }}>
          <Button
            component={RouterLink}
            to={`/agentic-ops/module/${first.slug}`}
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
          >
            {t("AgenticOpsHome.startFree", { number: first.number })}
          </Button>
        </Box>
      )}
    </Container>
  );
}
